import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Youtube, FileText, Link as LinkIcon, BookOpen, Users2, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ICON_BY_TYPE = {
  youtube: { Icon: Youtube, accent: "text-[#d8382b]", bg: "bg-[#d8382b]/10", label: "Video" },
  file: { Icon: FileText, accent: "text-terracotta", bg: "bg-terracotta/10", label: "File" },
  link: { Icon: LinkIcon, accent: "text-sage", bg: "bg-sage/10", label: "Link" },
};

/**
 * Latest resources strip on the student dashboard. Shows the 6 newest
 * chapter-scoped + group-scoped resources from `/me/whats-new`. Hidden when
 * empty. RTL-aware via `lang` prop.
 */
export default function WhatsNewBanner({ lang = "en" }) {
  const isAr = lang === "ar";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/me/whats-new", { params: { limit: 6 } })
      .then((r) => { if (!cancelled) setItems(r.data); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading || items.length === 0) return null;

  return (
    <section className="mb-8 animate-fade-up" data-testid="whats-new-banner">
      <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sage to-terracotta text-white flex items-center justify-center">
            <Sparkles className="w-4 h-4" strokeWidth={1.8} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-sage font-bold">
              {isAr ? "جديد" : "What's new"}
            </div>
            <h2 className="font-display text-lg sm:text-xl font-semibold tracking-tight">
              {isAr ? "أحدث الموارد من معلّميك" : "Fresh from your teachers"}
            </h2>
          </div>
        </div>
        <span className="text-xs text-muted-foreground">
          {isAr ? `${items.length} عنصر` : `${items.length} items`}
        </span>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory" data-testid="whats-new-row">
        {items.map((r) => <NewItemCard key={r.resource_id} resource={r} isAr={isAr} />)}
      </div>
    </section>
  );
}

function NewItemCard({ resource: r, isAr }) {
  const meta = ICON_BY_TYPE[r.type] || ICON_BY_TYPE.link;
  const Icon = meta.Icon;
  const fileUrl = r.file_id ? `${BACKEND}/api/files/${r.file_id}` : null;
  const externalHref = r.type === "file" ? fileUrl : (r.youtube_url || r.external_url);
  const isChapter = r.scope === "chapter" && r.chapter_id;
  // Chapter resources deep-link to the chapter Resources tab; group resources open externally.
  const internalTo = isChapter ? `/chapter/${r.chapter_id}?tab=resources` : null;

  const content = (
    <article
      data-testid={`whats-new-${r.resource_id}`}
      className="snap-start min-w-[260px] max-w-[280px] flex-shrink-0 bg-white border border-border rounded-2xl p-4 hover:border-sage/40 hover:shadow-md transition-all flex flex-col gap-3"
    >
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg ${meta.bg} ${meta.accent} flex items-center justify-center shrink-0`}>
          <Icon className="w-4 h-4" strokeWidth={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
            {meta.label}{r.created_at ? ` · ${ago(r.created_at, isAr)}` : ""}
          </div>
          <div className="font-medium text-sm leading-snug line-clamp-2 mt-0.5" title={r.title}>
            {r.title}
          </div>
        </div>
      </div>

      {r.type === "youtube" && r.youtube_id && (
        <div className="aspect-video rounded-lg overflow-hidden bg-bone border border-border">
          <iframe
            src={`https://www.youtube.com/embed/${r.youtube_id}`}
            title={r.title}
            className="w-full h-full pointer-events-none"
            allowFullScreen
          />
        </div>
      )}

      <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap mt-auto">
        {isChapter && (
          <span className="inline-flex items-center gap-1 bg-sage/10 text-sage px-1.5 py-0.5 rounded font-medium">
            <BookOpen className="w-3 h-3" />
            {isAr ? "فصل" : "Ch"}{String(r.chapter_number ?? "").padStart(2, "0")}
            {r.chapter_title ? ` · ${truncate(r.chapter_title, 18)}` : ""}
          </span>
        )}
        {r.scope === "group" && r.group_name && (
          <span className="inline-flex items-center gap-1 bg-terracotta/10 text-terracotta px-1.5 py-0.5 rounded font-medium">
            <Users2 className="w-3 h-3" />{truncate(r.group_name, 18)}
          </span>
        )}
        <ArrowRight className="w-3.5 h-3.5 ms-auto text-sage opacity-70" />
      </div>
    </article>
  );

  if (internalTo) {
    return <Link to={internalTo} data-testid={`whats-new-link-${r.resource_id}`} className="contents">{content}</Link>;
  }
  if (externalHref) {
    return (
      <a
        href={externalHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`whats-new-link-${r.resource_id}`}
        className="contents"
      >
        {content}
      </a>
    );
  }
  return content;
}

function truncate(s, n) {
  s = String(s || "");
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}

function ago(iso, isAr) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const mins = Math.max(1, Math.floor((Date.now() - d.getTime()) / 60000));
  if (mins < 60) return isAr ? `قبل ${mins}د` : `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return isAr ? `قبل ${hrs}س` : `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return isAr ? `قبل ${days} يوم` : `${days}d ago`;
  return d.toLocaleDateString();
}
